import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  RemoveAlertDialog,
  useDisclosure,
} from '@wegroup/design-system';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  InsuranceDetailSchema,
  InsuranceDetailFormValues,
} from '../../../../types/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import AddInsuranceModalBody from './AddInsuranceModalBody';
import { usePutInsurancePolicyToReport } from '../../../../api/current-insurances/putInsurance';
import { v4, validate } from 'uuid';
import useAdvisoryReportCreationParams from '../../../../hooks/useCreationFlowSubject';
import {
  Coverage,
  InsurancePolicy,
  InsurancePolicyType,
} from '../../../../../../types/AdvisoryReport';
import AddInsuranceModalFooter from './AddInsuranceModalFooter';
import { isNil, omitBy } from 'lodash';
import AddInsuranceModalContextProvider from './AddInsuranceModalContextProvider';
import useInsurancePolicyModal, {
  Page,
} from '../../hooks/useInsurancePolicyModal';
import { format, formatISO, isValid } from 'date-fns';
import { Option } from '../../../../../../../../design-system/src/content/components/SearchPickerInput';
import { PartyType } from '../../../../../../../../../types/SMT';
import i18n from '../../../../../../../../../i18n';

const defaultValues = {
  name: '',
  premium: undefined,
  closed_at_distribution: true,
  due_date: undefined,
  start_date: undefined,
  policy_nr: '',
  description: '',
  documents: undefined,
  insurance_company: '',
  payment_interval: 'ANNUAL',
};

export const getPolicyModalDefaultValues = (
  initialData?: Partial<InsurancePolicy>,
) => {
  // Omit data to remove all undefined or null values from the object
  // This will prevent form errors when a number or string is not nullable
  const omittedData = omitBy(initialData, isNil) as Partial<InsurancePolicy>;
  return {
    ...defaultValues,
    ...omittedData,
    insurance_company:
      omittedData.coverages?.[0]?.insurance_company ||
      omittedData.insurance_company ||
      '',
    due_date:
      omittedData.due_date &&
      isValid(new Date(`${new Date().getFullYear()}-${omittedData.due_date}`))
        ? formatISO(
            new Date(`${new Date().getFullYear()}-${omittedData.due_date}`),
          )
        : undefined,
    coverages:
      omittedData.coverages?.map(
        ({ description, risk_object, risk_object_id, ...other }: Coverage) => ({
          description: description || '',
          premium: undefined,
          insured_value: undefined,
          risk_object: risk_object || i18n.t('OTHER'),
          risk_object_id: risk_object_id || 'OTHER',
          ...omitBy(other, isNil),
        }),
      ) || [],
  };
};

interface Props {
  defaultIsOpenCoverage?: boolean;
  isOpen: boolean;
  initialPage?: Page;
  initialData?: Partial<InsurancePolicy>;
  onClose: () => void;
  onSubmit?: (insruancePolicy: InsurancePolicy) => void;
  mode: 'ADD' | 'EDIT';
  type: InsurancePolicyType;
  customRiskObjects?: Option[];
  controlled?: boolean;
  partyType?: PartyType;
  hideAIBanner?: boolean;
}

const AddInsuranceModal: React.FC<Props> = ({
  onClose,
  isOpen,
  mode,
  partyType,
  controlled,
  defaultIsOpenCoverage,
  initialPage,
  customRiskObjects = [],
  initialData,
  onSubmit,
  hideAIBanner,
  type,
}) => {
  const { t } = useTranslation();
  const closeAlert = useDisclosure();
  const { reportId } = useAdvisoryReportCreationParams();
  const putInsurance = usePutInsurancePolicyToReport();
  const methods = useForm<InsuranceDetailFormValues>({
    defaultValues: getPolicyModalDefaultValues(initialData),
    resolver: yupResolver(InsuranceDetailSchema),
  });
  const insuranceModalProps = useInsurancePolicyModal(
    methods.control,
    initialPage,
  );

  const handleClose = () => {
    methods.reset(getPolicyModalDefaultValues(initialData));
    onClose();
    // navigate to iniital page
    if (initialPage) insuranceModalProps.setPage(initialPage);
    else insuranceModalProps.setPage({ type: 'INSURANCE' });
  };

  const handleSubmit = methods.handleSubmit((values) => {
    // When the risk_object input has value OTHER, it will take the value of the risk_object_text input instead
    const coverages = (values.coverages || []).map(
      ({ risk_object_id, ...other }) => ({
        risk_object_id: validate(risk_object_id || '')
          ? risk_object_id
          : undefined,
        ...other,
      }),
    );

    const insurancePolicy = {
      ...values,
      id: initialData?.id || v4(),
      type:
        initialData?.type ||
        (type === 'CURRENT-INSURANCE' ? 'ACTIVE' : 'INACTIVE'),
      coverages,
      due_date: values.due_date
        ? format(new Date(values.due_date), 'MM-dd')
        : undefined,
      start_date: values.start_date
        ? format(new Date(values.start_date), 'yyyy-MM-dd')
        : undefined,
      documents: values.documents || [],
    };
    delete insurancePolicy['external_refs'];

    if (controlled) {
      onSubmit?.(insurancePolicy);
    } else {
      putInsurance.mutate({
        insurancePolicy,
        reportId,
      });
    }

    handleClose();
  });

  return (
    <>
      <RemoveAlertDialog
        onClose={closeAlert.onClose}
        isOpen={closeAlert.isOpen}
        onConfirm={handleClose}
        title={t('ARE-YOU-SURE')}
        description={t(
          'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCE_MODAL_ARE-YOU-SURE',
        )}
        confirmButtonText={t('CLOSE')}
      />
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={methods.formState.isDirty ? closeAlert.onOpen : handleClose}
          size="5xl"
        >
          <ModalOverlay />
          <AddInsuranceModalContextProvider
            defaultIsOpenCoverage={defaultIsOpenCoverage}
            initialData={initialData}
            customRiskObjects={customRiskObjects}
          >
            <FormProvider {...methods}>
              <ModalContent h="3xl" maxH="90%">
                <ModalHeader>
                  {t(
                    `ADVISORY-REPORTS_CREATION-FLOW_${type}_MODAL_${mode}_TITLE`,
                  )}
                </ModalHeader>
                <ModalCloseButton />
                <AddInsuranceModalBody
                  type={type}
                  mode={mode}
                  insuranceModalProps={insuranceModalProps}
                  partyType={partyType}
                  hideAIBanner={hideAIBanner}
                />
                <AddInsuranceModalFooter
                  onSubmit={handleSubmit}
                  onClose={
                    methods.formState.isDirty ? closeAlert.onOpen : handleClose
                  }
                  insuranceModalProps={insuranceModalProps}
                />
              </ModalContent>
            </FormProvider>
          </AddInsuranceModalContextProvider>
        </Modal>
      )}
    </>
  );
};

export default AddInsuranceModal;
